import { useLocale } from "@/hooks/useLocale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import {
  History,
  Menu,
  MessageSquareDashed,
  MessageSquarePlus,
  PanelRightClose,
  PanelRightOpen,
  SearchIcon,
  X,
} from "lucide-react";
import { useEffect } from "react";
import { useMediaQuery } from "usehooks-ts";
import { AnimatePresence, motion } from "framer-motion";
import { Conversations } from "@/pages/(home)/(my-space)/ai-assistant/_components/conversations";
import { Drawer } from "@/components/ui/Drawer";
import { useAIAssistantContext } from "./context";
import type { ReactNode } from "react";

interface IProps {
  startNewChat: () => void;
  conversationsSlot?: ReactNode;
  /** Set false when the surface hosts its own temp-chat control (the company
   *  assistant puts it in the canvas header instead of this rail). */
  showTempChat?: boolean;
}

/** Wraps a collapsed-rail button with a tooltip so its icon-only action stays
 *  discoverable. When `show` is false (sidebar open, label visible) it renders
 *  the child untouched. */
const CollapsedTooltip = ({
  show,
  label,
  children,
}: {
  show: boolean;
  label: string;
  children: ReactNode;
}) => {
  const { byDir } = useLocale();
  if (!show) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={byDir("left", "right")} className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

export const AIAssistantSidebar = ({
  startNewChat,
  conversationsSlot,
  showTempChat = true,
}: IProps) => {
  const { t } = useTranslation("my-space");
  const { isSidebarOpen, setIsSidebarOpen } = useAIAssistantContext();
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");
  useEffect(() => {
    setIsSidebarOpen(!isSmallScreen);
  }, [isSmallScreen, setIsSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const SidebarContent = (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col gap-3 h-full">
        <Header isOpen={isSidebarOpen} toggleOpenState={toggleSidebar} />
        <AnimatePresence mode="wait">
          <div className="flex flex-col gap-1.5">
            <NewChatButton
              startNewChat={startNewChat}
              isSidebarOpen={isSidebarOpen}
            />
            {showTempChat && <TempChatButton isSidebarOpen={isSidebarOpen} />}
          </div>
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {(isSidebarOpen || isSmallScreen) &&
            (conversationsSlot ?? <Conversations />)}
        </AnimatePresence>
        {!isSidebarOpen && !isSmallScreen && (
          <>
            <CollapsedTooltip show label={t("ai_assistant.search_chats")}>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label={t("ai_assistant.search_chats")}
                className="size-9 w-full"
                onClick={() => setIsSidebarOpen(true)}
              >
                <SearchIcon size={18} className="text-muted-foreground" />
              </Button>
            </CollapsedTooltip>
            <div
              className="bg-border mx-auto h-px w-6 shrink-0"
              aria-hidden="true"
            />
            <CollapsedTooltip show label={t("ai_assistant.show_history")}>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("ai_assistant.show_history")}
                className="text-muted-foreground size-9 w-full"
                onClick={() => setIsSidebarOpen(true)}
              >
                <History size={18} />
              </Button>
            </CollapsedTooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );

  if (isSmallScreen)
    return (
      <Drawer
        hideCloseButton
        size="md"
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      >
        {SidebarContent}
      </Drawer>
    );

  return (
    <motion.aside
      animate={{ width: isSidebarOpen ? 340 : 60 }}
      transition={{ type: "spring", stiffness: 400, damping: 40 }}
      className="bg-surface border-s p-2 flex flex-col overflow-hidden h-full min-h-0"
    >
      {SidebarContent}
    </motion.aside>
  );
};

const Header = ({
  isOpen,
  toggleOpenState,
}: {
  isOpen: boolean;
  toggleOpenState: () => void;
}) => {
  const { t } = useTranslation("my-space");
  const RightIcon = isOpen ? PanelRightClose : PanelRightOpen;

  return (
    <div className="w-full flex justify-between items-center">
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.h6
            key="title"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="flex gap-2 items-center text-xl font-semibold ai-text-gradient whitespace-nowrap"
          >
            {t("ui.ai_assistant")}
          </motion.h6>
        )}
      </AnimatePresence>
      <CollapsedTooltip show={!isOpen} label={t("ai_assistant.open_sidebar")}>
        <button
          onClick={toggleOpenState}
          aria-label={isOpen ? t("ui.close_sidebar") : t("ui.open_sidebar")}
          className="p-2 rounded hover:bg-gray-100 text-primary transition-colors"
        >
          <RightIcon size={22} className={cn("transition-transform")} />
        </button>
      </CollapsedTooltip>
    </div>
  );
};

const NewChatButton = ({
  startNewChat,
  isSidebarOpen = false,
}: {
  startNewChat: () => void;
  isSidebarOpen: boolean;
}) => {
  const { t } = useTranslation("my-space");
  const { setIsSidebarOpen } = useAIAssistantContext();
  // On desktop the rail sits beside the canvas, so starting a chat has no
  // reason to collapse your history. On small screens it's an overlay drawer —
  // there, it has to get out of the way of the chat you just started.
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");
  return (
    <CollapsedTooltip show={!isSidebarOpen} label={t("ai_assistant.new_chat")}>
      <Button
        className="moving-gradient-bg gap-2 h-9 w-full shrink-0"
        size="sm"
        onClick={() => {
          startNewChat();
          if (isSmallScreen) setIsSidebarOpen(false);
        }}
      >
        <MessageSquarePlus size={18} />{" "}
        {isSidebarOpen && t("ai_assistant.new_chat")}
      </Button>
    </CollapsedTooltip>
  );
};

const TempChatButton = ({ isSidebarOpen }: { isSidebarOpen: boolean }) => {
  const { t } = useTranslation("my-space");
  const { isTempChat, startTempChat, stopTempChat, setIsSidebarOpen } =
    useAIAssistantContext();
  const Icon = isTempChat ? X : MessageSquareDashed;
  const handleClick = () => {
    if (isTempChat) stopTempChat();
    else startTempChat();
    setIsSidebarOpen(false);
  };
  return (
    <CollapsedTooltip
      show={!isSidebarOpen}
      label={isTempChat ? t("ui.stop_temp_chat") : t("ui.temp_chat")}
    >
      {/* Secondary by design: two equally loud full-width buttons made the rail
          read as two primary actions. Temp Chat only gains the brand outline
          once it's the mode you're in. */}
      <Button
        size="sm"
        variant={isTempChat ? "outline-primary" : "ghost"}
        aria-pressed={isTempChat}
        className={cn(
          "h-8 w-full shrink-0 gap-2 text-xs",
          !isTempChat && "text-muted-foreground hover:text-foreground",
        )}
        onClick={handleClick}
      >
        <Icon size={14} />
        {isSidebarOpen &&
          (isTempChat ? t("ui.stop_temp_chat") : t("ui.temp_chat"))}
      </Button>
    </CollapsedTooltip>
  );
};

interface MobileMenuTriggerProps {
  rightContent?: ReactNode;
  title?: string;
}

export const MobileMenuTrigger = ({
  rightContent,
  title,
}: MobileMenuTriggerProps = {}) => {
  const { t } = useTranslation("my-space");
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");
  const { setIsSidebarOpen } = useAIAssistantContext();
  if (!isSmallScreen && !rightContent && !title) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b bg-white shrink-0">
      {title && (
        <h2 className="text-sm font-medium text-slate-700 truncate flex-1">
          {title}
        </h2>
      )}
      {!title && <div className="flex-1" />}
      {rightContent}
      {isSmallScreen && (
        <Button
          size="icon"
          variant="outline"
          aria-label={t("ai_assistant.open_history")}
          data-tooltip-id="hrm-tooltip"
          data-tooltip-content={t("ai_assistant.open_history")}
          className="rounded-full size-8"
          onClick={() => setIsSidebarOpen(true)}
        >
          <Menu size={16} />
        </Button>
      )}
    </div>
  );
};
